export default function () {
  return {
    name: "Performance Anxiety Screening",
    description:
      "We can help you find out if medication might help with your performance anxiety",
    version: "2026070801",
    service_code: "PERFORMANCE_ANXIETY",
    live: false,
    staged: true,
    questions: [
      {
        title: "Why are you interested in medication for performance anxiety?",
        description:
          "Check all that apply. This helps us understand and recommend the right treatment for you.",
        type: "checkbox",
        choices: [
          {
            label: "Have tried other treatment options without success",
          },
          {
            label: "Looking for a medication to treat your anxiety symptoms",
          },
          {
            label: "Interested in treatment for flight anxiety",
          },
          {
            label: "Interested in treatment for driving anxiety",
          },
          {
            label: "Heard about medications from someone around you",
          },
          {
            label: "Heard about medications in the news",
          },
          {
            label: "Research on the internet",
          },
          {
            label: "Other",
            children: [
              {
                title: "Please tell us why you're interested...",
                type: "text",
                code: "why-interested-other",
              },
            ],
          },
        ],
      },
      {
        title: "Do you have any medical conditions?",
        description:
          "Include even any minor or recent conditions or symptoms. To give you the best care we need to know your full history.",
        type: "radio",
        choices: [
          {
            label: "Yes",
            children: [
              {
                title: "What medical conditions do you have?",
                description:
                  "Even include things you think you have whether or not you've been diagnosed by your doctor. We will message or call you if we need more information.",
                code: "medical-conditions",
                type: "textarea",
              },
            ],
          },
          {
            label: "No, don't have any medical conditions",
            silent: true,
          },
        ],
      },
      {
        title: "Have you ever had any surgeries or hospitalizations?",
        description:
          "Include any significant emergency room visits, hospital stays, or surgeries, including cosmetic surgeries.",
        type: "radio",
        choices: [
          {
            label: "Yes",
            children: [
              {
                title: "What surgeries or hospitalizations have you had?",
                description:
                  "List all the procedures and events and try to remember when they occurred.",
                code: "hospitalizations",
                type: "textarea",
              },
            ],
          },
          {
            label: "No, never had surgery or been hospitalized",
            silent: true,
          },
        ],
      },
      {
        title: "Do you currently take any medicines, herbals, or supplements?",
        description:
          "Please include any medicines you finished recently, including topical medicines, and any injections, vitamins, herbal remedies, or any other products you use.",
        type: "radio",
        code: "medications-radio",
        choices: [
          {
            label: "Yes",
            children: [
              {
                title: "What medicines, herbals, or supplements do you use?",
                description:
                  "Please list the dose and why you are taking each medicine or supplement.",
                code: "medications",
                type: "textarea", // @todo multi-select autocomplete
              },
            ],
          },
          {
            label:
              "No, not taking any medicines, supplements, herbals, or other prescriptions",
            silent: true,
          },
        ],
      },
      {
        title: "Do you have any allergies or medication reactions?",
        code: "allergies-medication-reactions",
        description:
          "Please include any allergies or major reactions to medicines.",
        type: "radio",
        choices: [
          {
            label: "Yes",
            children: [
              {
                title: "What allergies do you have?",
                description:
                  "List the allergies and if possible, the severity of any reaction you have had.",
                code: "allergies",
                type: "textarea", // @todo multi-select autocomplete
              },
            ],
          },
          {
            label: "No, don't have any allergies or medication reactions",
            silent: true,
          },
        ],
      },
      {
        title: "Do any of the following currently apply to you?",
        description:
          "Certain conditions can complicate diagnosis, increase risks, or change the recommended treatments so it's critical we know everything going on with your health.",
        type: "checkbox",
        choices: [
          {
            label:
              "Experience sweatiness, a rapid heart rate, or feeling shaky during periods of anxiety?",
          },
          {
            label:
              "Fear or anxiety about social situations that may involve scrutiny by others?",
          },
          {
            label:
              "Fear that you will act in a way or show anxiety symptoms that will be negatively evaluated by another individual?",
          },
          {
            label:
              "Almost always experience fear or anxiety in social situations?",
          },
          {
            label: "Avoid social situations or tasks?",
          },
          {
            label:
              "Feel your anxiety is out of proportion to the actual threat?",
          },
          {
            label:
              "Only experience anxiety before or during giving a performance such as speaking in public, or speaking during a meeting, a presentation, an interview or a date?",
          },
          {
            label: "Experience anxiety during air travel?",
          },
          {
            label: "Experience anxiety driving or riding in a car?",
          },
          {
            label: "Fear of seeing the doctor or dentist?",
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },
      {
        title: "Have any of the following affected you?",
        description:
          "Certain conditions can complicate diagnosis, increase risks, or change the recommended treatments so it's critical we know everything going on with your health.",
        type: "checkbox",
        choices: [
          {
            label: "Fear, anxiety or avoidance has lasted > 6 months?",
          },
          {
            label:
              "Fear or anxiety has resulted in significant social or professional limitations?",
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },
      {
        title:
          "Could you tell us in your own words what kind of problems you are experiencing and how you feel?",
        description:
          "Please provide as much information as possible, and include any questions or concerns you might have so your doctor can perform a thorough evaluation.",
        type: "textarea",
        code: "chief-complaint",
        minLength: 10,
      },
      {
        title: "Do you have any of the following conditions?",
        description:
          "Even if they were in the past, certain conditions can complicate diagnosis, increase risks, or change the recommended treatments so it's critical we know your full history.",
        type: "checkbox",
        code: "conditions",
        choices: [
          {
            label:
              "Any problems with poor circulation, or cold or blue extremities?",
          },
          {
            label: "Are you pregnant?",
          },
          {
            label: "Are you breastfeeding?",
          },
          {
            label: "Do you take any medication to lower blood pressure?",
            children: [
              {
                title:
                  "Please list your blood pressure medications and dosages if you know them.",
                description:
                  "list your blood pressure medications and dosages if you know them",
                code: "blood-pressure-medication-details",
                type: "textarea",
              },
            ],
          },
          {
            label: "Any liver problems?",
            children: [
              {
                title: "Please tell us a bit more about your liver problems.",
                description: "tell us a bit more about your liver problems.",
                code: "liver-problem-details",
                type: "textarea",
              },
            ],
          },
          {
            label: "Any kidney problems?",
            children: [
              {
                title: "Please tell us a bit more about your kidney problems.",
                description: "tell us a bit more about your kidney problems.",
                code: "kidney-problem-details",
                type: "textarea",
              },
            ],
          },
          {
            label:
              "Are you currently experiencing any symptoms of sexual dysfunction?",
          },
          {
            label: "Do you have glaucoma?",
          },
          {
            label:
              "Ever been diagnosed with any mental disorders such as bipolar disorder or schizophrenia?",
            children: [
              {
                title: "What mental disorders have you been diagnosed with?",
                description:
                  "List the disordera and if possible, when you were diagnosed.",
                code: "mental-disorders",
                type: "textarea",
              },
            ],
          },
          {
            label:
              "Ever been diagnosed with any neurological disorder such as Parkinson’s disease or dementia?",
            children: [
              {
                title:
                  "What neurological disorders have you been diagnosed with?",
                description:
                  "List the disorder and if possible, when you were diagnosed.",
                code: "neurological-disorders",
                type: "textarea",
              },
            ],
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },

      {
        title: "Have you ever had any of the following conditions?",
        description:
          "Even if they were in the past, certain conditions can complicate diagnosis, increase risks, or change the recommended treatments so it's critical we know your full history.",
        type: "checkbox",
        code: "conditions-continued",
        choices: [
          {
            label: "Any history of asthma, or episodes of wheezing?",
            children: [
              {
                title:
                  "Tell us more - are you currently using an inhaler or asthma medication?",
                description:
                  "Are you currently using an inhaler or asthma medication?",
                code: "history-asthma-detail",
                type: "textarea",
              },
            ],
          },
          {
            label: "Any history of lung problems such as COPD or emphysema?",
            children: [
              {
                title: "Tell us more about the lung problem / emphysema...",
                description: "Tell us more about the lung problem / emphysema",
                code: "history-lung-problem-detail",
                type: "textarea",
              },
            ],
          },
          {
            label:
              "Any history of heart attack, or any heart condition / problem?",
            children: [
              {
                title: "Tell us more about the heart condition / problem...",
                description: "Tell us more about the heart condition / problem",
                code: "history-heart-condition-detail",
                type: "textarea",
              },
            ],
          },
          {
            label: "Any history of Wolf-Parkinson-White Syndrome?",
          },
          {
            label:
              "Any history of shortness of breath with physical activity, wheezing, chest tightness, or chronic cough?",
            children: [
              {
                title:
                  "Tell us more - when and how do you experience shortness or breath, wheezing or cough?",
                description:
                  "when and how do you experience shortness or breath, wheezing or cough?",
                code: "history-sob-wheezing-detail",
                type: "textarea",
              },
            ],
          },
          {
            label: "Any history of fainting episodes, or slow heart rate?",
          },
          {
            label: "Any history of insulin use for diabetes?",
          },
          {
            label: "Any history of low blood sugar or hypoglycemia?",
          },
          {
            label: "Any history of myasthenia gravis?",
          },
          {
            label: "Any history of Raynaud's disease?",
            children: [
              {
                code: "history-raynauds-detail",
                type: "textarea",
                title:
                  "Have you been officially diagnosed with Raynaud's disease? There can be issues with beta-blocker if so. Please tell us more.",
                description: "Have you been diagnosed with Raynaud's disease?",
              },
            ],
          },
          {
            label: "Any history of psoriasis?",
          },
          {
            label:
              "Any history of feeling light headed or dizzy upon standing from a seated position?",
            children: [
              {
                title: "Tell us more - when do you feel light headed or dizzy?",
                description: "When do you feel light headed or dizzy?",
                code: "history-light-headed-dizzy-detail",
                type: "textarea",
              },
            ],
          },
          {
            label: "Any history of gastric bypass surgery?",
          },
          {
            label: "Any history of hyperthyroidism?",
          },
          {
            label:
              "Any close relatives with a history of cardiac arrhythmias? (Irregular heart rhythm, etc.)",
            children: [
              {
                title: "Which relative? What cardiac issue did thay have?",
                description:
                  "Which relative? What cardiac issue did thay have?",
                code: "history-relative-cardiac-arrhythmia",
                type: "textarea",
              },
            ],
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },
      {
        title:
          "What is the TOP number of your blood pressure (systolic reading) taken in the last 6 months?",
        description:
          "In order to provide a safe and effective treatment plan, we need an accurate blood pressure measurement. Blood pressure results are a higher number over another lower number; for example: 115/75. Please start by entering the higher number below (you’ll enter the lower number on the next page).",
        code: "systolic",
        type: "bloodpressure-systolic",
        choices: [
          {
            label: "160 or higher",
            rightSideColorLabel: {
              text: "Very High",
              color: "#740a09",
            },
          },
          {
            label: "140-159",
            rightSideColorLabel: {
              text: "High",
              color: "#d94442",
            },
          },
          {
            label: "120-139",
            rightSideColorLabel: {
              text: "Normal",
              color: "#45bd57",
            },
          },
          {
            label: "95-119",
            rightSideColorLabel: {
              text: "Lower",
              color: "#6ecba4",
            },
          },
          {
            label: "Under 95",
            rightSideColorLabel: {
              text: "Low",
              color: "#37a0d8",
            },
          },
        ],
      },
      {
        title:
          "What is the BOTTOM number of your blood pressure (diastolic reading) taken in the last 6 months?",
        description:
          "Please select the LOWER NUMBER carefully, it is very important to treat you safely.",
        code: "diastolic",
        type: "bloodpressure-diastolic",
        choices: [
          {
            label: "100 or over",
            rightSideColorLabel: {
              text: "Very High",
              color: "#740a09",
            },
          },
          {
            label: "90-99",
            rightSideColorLabel: {
              text: "High",
              color: "#d94442",
            },
          },
          {
            label: "80-90",
            rightSideColorLabel: {
              text: "Normal",
              color: "#45bd57",
            },
          },
          {
            label: "65-79",
            rightSideColorLabel: {
              text: "Lower",
              color: "#6ecba4",
            },
          },
          {
            label: "Under 65",
            rightSideColorLabel: {
              text: "Low",
              color: "#37a0d8",
            },
          },
        ],
      },
      {
        title: "Do any of the below apply to you?",
        type: "checkbox",
        code: "comorbid-psych",
        choices: [
          {
            label:
              "Are you worried about how you look? AND do you think about your appearance problems a lot and wish you could think about them less?",
          },
          {
            label:
              "Do you have any history of panic attacks - an abrupt surge of intense fear that peaks in minutes and may include chest pain or discomfort, feelings of choking, numbness or tingling sensations, a fear of dying, or a feeling of dizziness?",
          },
          {
            label:
              "Do you avoid public situations due to fear of developing anxiety spontaneously and having difficulty fleeing the situation, rather than because of fear of scrutiny and embarrassment?",
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },
      {
        title: "Have you ever: ",
        type: "checkbox",
        choices: [
          {
            label:
              "Felt you need to cut down on use of a substance (alcohol, cocaine, cannabis, etc)",
          },
          {
            label: "Been annoyed by people criticizing your use of substances?",
          },
          {
            label: "Felt guilty about your use of a substance?",
          },
          {
            label:
              "Needed a drink first thing in the morning as an eye-opener?",
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },
      {
        title:
          "Do you drink alcohol, smoke cigarettes, or use other substances?",
        description:
          "Please answer accurately. It's important we know to treat you safely",
        type: "checkbox",
        choices: [
          {
            label: "Do you use alcohol regularly?",
            children: [
              {
                title: "How many drinks a week, on average?",
                code: "alcohol-frequency",
                type: "text",
              },
            ],
          },
          {
            label: "Do you smoke or vape?",
            children: [
              {
                title: "How often do you smoke or vape a week, on average? ",
                code: "tobacco-frequency",
                type: "text",
              },
            ],
          },
          {
            label: "Any other substances?",
            children: [
              {
                title: "What substances do you use, and how frequently?",
                code: "substances-frequency",
                type: "textarea",
              },
            ],
          },
          {
            label: "None of the above",
            silent: true,
          },
        ],
      },
      {
        title:
          "Do you have any concerns about your mood, depression, anxiety or worrying?",
        code: "PHQ-GAD-2",
        type: "radio",
        choices: [
          {
            label: "No",
            silent: true,
          },
          {
            label: "Yes",
            children: [
              {
                title:
                  "Over the past two weeks, how often have you felt little interest or pleasure in doing things?",
                code: "PHQ2-1",
                type: "radio",
                choices: [
                  {
                    label: "Not At all",
                  },
                  {
                    label: "Several Days",
                  },
                  {
                    label: "More than Half the Days",
                  },
                  {
                    label: "Nearly Every Day",
                  },
                ],
              },
              {
                title:
                  "Over the past two weeks, how often have you felt down, depressed or hopeless?",
                type: "radio",
                code: "PHQ2-2",
                choices: [
                  {
                    label: "Not At all",
                  },
                  {
                    label: "Several Days",
                  },
                  {
                    label: "More than Half the Days",
                  },
                  {
                    label: "Nearly Every Day",
                  },
                ],
              },
              {
                title:
                  "Over the past two weeks, how often have you felt nervous, anxious, or on edge?",
                type: "radio",
                code: "GAD2-1",
                choices: [
                  {
                    label: "Not At all",
                  },
                  {
                    label: "Several Days",
                  },
                  {
                    label: "More than Half the Days",
                  },
                  {
                    label: "Nearly Every Day",
                  },
                ],
              },
              {
                title:
                  "Over the past two weeks, how often have you not been able to stop or control worrying?",
                code: "GAD2-2",
                type: "radio",
                choices: [
                  {
                    label: "Not At all",
                  },
                  {
                    label: "Several Days",
                  },
                  {
                    label: "More than Half the Days",
                  },
                  {
                    label: "Nearly Every Day",
                  },
                ],
              },
            ],
          },
        ],
      },

      {
        title: "Anything else you would like your doctor to know?",
        description:
          "Before we wrap up, any final things you'd like to tell your doctor? Feel free to ask questions as well.",

        type: "radio",
        choices: [
          {
            label: "Yes",
            children: [
              {
                title: "What would you like to tell your doctor?",
                code: "doctor-let-know",
                type: "textarea",
              },
            ],
          },
          {
            label: "No, we can move on",
            silent: true,
          },
        ],
      },

      {
        title: "How did you FIRST hear about Kick?",
        description:
          "This helps us understand how we can help more people like you.",
        type: "radio",
        code: "hear-about-kick",
        choices: [
          {
            label: "Facebook",
          },
          {
            label: "Google",
          },
          {
            label: "Instagram",
          },
          {
            label: "News Article",
          },
          {
            label: "Pinterest",
          },
          {
            label: "Podcast",
            children: [
              {
                title: "Please tell us which podcast...",
                code: "hear-about-kick-podcast-detail",
                type: "text",
              },
            ],
          },
          {
            label: "Reddit",
          },
          {
            label: "Streaming or Cable TV",
          },
          {
            label: "TikTok",
          },
          {
            label: "Twitter",
          },
          {
            label: "YouTube",
          },
          {
            label: "From a coworker or colleague",
          },
          {
            label: "From a friend or family member",
          },
          {
            label: "Other",
            children: [
              {
                title: "Please tell us where...",
                code: "hear-about-kick-other-detail",
                type: "text",
              },
            ],
          },
        ],
      },
      {
        title:
          "For how long did you know about us before making your purchase?",
        description: "",
        type: "radio",
        code: "time-known-before-purchase",
        choices: [
          {
            label: "A day",
          },
          {
            label: "A week",
          },
          {
            label: "1-2 weeks",
          },
          {
            label: "2-4 weeks",
          },
          {
            label: "1-3 months",
          },
          {
            label: "4-12 months",
          },
          {
            label: "More than a year",
          },
        ],
      },
      {
        title:
          "What made you decide to finally take action and complete a visit today?",
        description:
          "This helps us understand your motivations for choosing Kick.",
        type: "textarea",
        code: "visit-motivation",
      },
      {
        title: "Are you into MBTI? What's your type? (eg. INTJ, ESFP)",
        description: "If you know it, this helps us personalize your treatment plan to fit how your nervous system is wired",
        type: "radio",
        code: "mbti-known-yes-no",
        choices: [
          {
            label: "Yes",
            children: [
              {
                title: "What is your MBTI type?",
                code: "mbti-type",
                type: "radio",
                choices: [
                  {
                    label: "ISTJ - The Inspector",
                  },
                  {
                    label: "ISFJ - The Protector",
                  },
                  {
                    label: "INFJ - The Counselor",
                  },
                  {
                    label: "INTJ - The Mastermind",
                  },
                  {
                    label: "ISTP - The Craftsman",
                  },
                  {
                    label: "ISFP - The Composer",
                  },
                  {
                    label: "INFP - The Healer",
                  },
                  {
                    label: "INTP - The Architect",
                  },
                  {
                    label: "ESTP - The Dynamo",
                  },
                  {
                    label: "ESFP - The Performer",
                  },
                  {
                    label: "ENFP - The Champion",
                  },
                  {
                    label: "ENTP - The Inventor",
                  },
                  {
                    label: "ESTJ - The Supervisor",
                  },
                  {
                    label: "ESFJ - The Provider",
                  },
                  {
                    label: "ENFJ - The Teacher",
                  },
                  {
                    label: "ENTJ - The Commander",
                  },
                ],
              },
            ],
          },
          {
            label: "No",
            silent: true,
          },
        ],
      },
    ],
  };
}

// -------------------------------------------------------
//
// CHANGELOG (Reverse chronological order)
//
// -------------------------------------------------------
//
// Version 2026070801
// - Added codes "hear-about-kick" and "time-known-before-purchase" to the post-purchase survey questions so the answers can be read into the PostHog purchase-funnel events (and written to the person profile)
//
// Version 2026061801
// - Added code "visit-motivation" to the "What made you decide to finally take action" question so triage can read it and combine it with the chief complaint for a more holistic view of the patient's issues in their own words

// Version 2025112301
// - Added MBTI personality type question with Yes/No radio and child question showing all 16 types
//
// Version 2025110101
// - Added Pinterest, News Article, and Streaming or Cable TV to hear about Kick question
//
// Version 2025011301
// - Added minLength to textarea questions
//
// Version 2025100901
// - Added question code 'allergies-medication-reactions' to allergies question for EMR red text highlighting
//
// Version 2025042101
// - Add post purchase survey questions
//
// Version 2024042501
// - Addd child question to ask for more details about Raynaud's disease
//
// Version 2024041501
// - Rename Raynaud's syndrome to Raynaud's disease as people were not understanding the term syndrome
//
// Version 2024040201
// - Added Raynaud's syndrome as an explicit condition to ask about under medical conditions
//
// Version 2023120701
// - Added follow up child details for Any Kidney Problems? question
// - Added follow up child details for Any Liver Problems? question
//
// Version 2023080201
// - Added flying and driving anxiety to first question
// - Added fear of flying, driving and doctors to question 6
//
// Version 2023063001
// - Added follow-up question about blood pressure medications
// - Added follow-up question about lung problems
// - Moved family history of cardiac arrhythmias to the end of the list
//
// Version 20230501901
// - Fix type of it critical
//
// Version 2023012201
// - Added follow up question to close relatives with a history of cardiac arrhythmias on question 10 - Conditions
// - Added follow up question to shortness of breath, wheezing, chest tightness, or chronic cough on question 10 - Conditions
// - Added follow up question to history of feeling light headed or dizzy upon standing on question 10 - Conditions
//
// Version 2023010201
// - Added follow up question to Asthma history on question 10 - Conditions
// - Added follow up question to Heart condition / problem on question 10 - Conditions
//
// Version 2022120601
// - Added question at then end for asking doctor a question
//
// Version 2022012201
// - Change GAD2-2 question to "Over the past two weeks, how often have you not been able to stop or control worrying?"
//
//
// -------------------------------------------------------
